<script lang="ts" setup>
import { api } from '@/api';
import {
  type CalibrationDataDto,
  type CalibrationDataOverviewDtoLastReportedByDevice,
  type CalibrationDataOverviewDtoNewDownloadedCalibration,
  type RequestCalibrationDataSupportForDeviceRequest,
  SupportRequestDtoTypeEnum,
} from '@/api/generator';
import Dialog from '@/components/Dialog/Dialog.vue';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import TextField from '@/components/forms/TextField/TextField.vue';
import { useVeeForm } from '@/components/forms/useVeeForm.ts';
import { success as successIcon } from '@/components/icons/icons.ts';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import { formBackendErrorHandler } from '@/errorHandling';
import { useQaEquipmentStore } from '@/stores/qaEquipmentStore.ts';
import { I18nKey } from '@/utils/typings.ts';
import CalibrationInformation from '@/views/qaEquipment/components/CalibrationInformation.vue';
import {
  getModelLabel,
  getTypeLabel,
  isRegistrablePtwQaEquipment,
} from '@/views/qaEquipment/utils';
import { mdiEmail, mdiPhone } from '@mdi/js';
import { ref } from 'vue';

const modelValue = defineModel<boolean>({ type: Boolean, default: false });

type QaEquipmentSupportRequestCalibrationInvalidDialogProps = {
  newCalibrationData?: CalibrationDataOverviewDtoNewDownloadedCalibration | null;
  currentCalibrationData: CalibrationDataDto;
  lastReportedByDevice?: CalibrationDataOverviewDtoLastReportedByDevice | null;
};
defineProps<QaEquipmentSupportRequestCalibrationInvalidDialogProps>();

const qaEquipmentStore = useQaEquipmentStore();

const emailSentSuccessfully = ref(false);

const { form } = useVeeForm<RequestCalibrationDataSupportForDeviceRequest>({
  mode: 'edit',
  options: {
    initialValues: {
      id: qaEquipmentStore.deviceForRegistration?.id,
      supportRequestDto: {
        $type:
          SupportRequestDtoTypeEnum.PtwPtw100AppZeroApplicationSharedDeviceComponentDtoSupportRequestDtoPtwPtw100AppZeroApplicationShared,
        message: undefined,
      },
    },
  },
});
const { handleSubmit, resetForm } = form;
const submit = handleSubmit(async (requestCalibrationDataSupportForDeviceRequest) => {
  const { success } = await api.qaEquipmentCloudApi.requestCalibrationDataSupportForDevice(
    requestCalibrationDataSupportForDeviceRequest,
    [formBackendErrorHandler(form)],
  );

  if (success) {
    emailSentSuccessfully.value = true;
    qaEquipmentStore.setCsoPortalFullErrorResponse(undefined);
  }
});

function finaliseSupportRequest() {
  modelValue.value = false;
  resetForm();
  emailSentSuccessfully.value = false;
}
function closeDialog() {
  modelValue.value = false;
}
</script>

<template>
  <Dialog
    v-if="
      qaEquipmentStore.deviceForRegistration &&
      isRegistrablePtwQaEquipment(qaEquipmentStore.deviceForRegistration)
    "
    v-model="modelValue"
    aria-label="RequestSupportDialog"
    hideClose
  >
    <template #title>{{ $t('ContactPtwCustomerSupport') }}</template>
    <template #text>
      <div class="d-flex align-center mb-4">
        <v-icon :icon="mdiPhone" class="text-secondary mr-2"></v-icon>
        <span>+49 761 49055-0</span>
      </div>
      <div class="d-flex align-center mb-8">
        <v-icon :icon="mdiEmail" class="text-secondary mr-2"></v-icon>
        <span>support@ptwdosimetry.com</span>
      </div>

      <FormLabel :label="`${$t('Name')} | ${$t('ID')}`">
        <IdentifierLabel
          :label="qaEquipmentStore.deviceForRegistration.name"
          :identifier="qaEquipmentStore.deviceForRegistration.iuid"
        />
      </FormLabel>
      <v-row class="mt-2">
        <v-col>
          <TextField
            :label="$t('Type_one')"
            :modelValue="$t(getTypeLabel(qaEquipmentStore.deviceForRegistration) as I18nKey)"
            :mode="'details'"
          />
        </v-col>
        <v-col>
          <TextField
            :label="$t('Model')"
            :modelValue="getModelLabel(qaEquipmentStore.deviceForRegistration)"
            :mode="'details'"
          />
        </v-col>
        <v-col>
          <TextField
            :label="$t('ArticleNumber')"
            :modelValue="`${qaEquipmentStore.deviceForRegistration.typeNumber}`"
            :mode="'details'"
          />
        </v-col>
        <v-col>
          <TextField
            :label="$t('SerialNumber')"
            :modelValue="qaEquipmentStore.deviceForRegistration.serialNumber"
            :mode="'details'"
          />
        </v-col>
      </v-row>

      <v-divider class="my-4" />
      <v-row>
        <v-col>
          <h5>{{ $t('CurrentCalibrationCertificate') }}</h5>
          <CalibrationInformation
            :calibrationData="currentCalibrationData"
            hideActivationDate
            hideCalibratedBy
            colorizeState
            expanded
          />
        </v-col>

        <v-col>
          <h5>{{ $t('NewCalibrationCertificate') }}</h5>
          <CalibrationInformation
            :calibrationData="newCalibrationData"
            hideActivationDate
            hideCalibratedBy
            expanded
          />
        </v-col>
      </v-row>

      <v-row v-if="lastReportedByDevice">
        <v-col>
          <h5>{{ $t('LastCalibrationReportedByDevice') }}</h5>
          <CalibrationInformation
            :calibrationData="lastReportedByDevice"
            hideActivationDate
            hideCalibratedBy
            hideCalibrationState
            showLastModifiedDate
          />
        </v-col>
      </v-row>

      <VeeTextarea
        :label="$t('Message')"
        :mode="emailSentSuccessfully ? 'details' : 'edit'"
        :placeholder="$t('SupportRequestMessagePlaceholder')"
        class="gutterTop"
        name="supportRequestDto.message"
        rows="8"
      />
      <v-alert
        v-if="emailSentSuccessfully"
        :icon="successIcon"
        :text="$t('MessageSentWeWillGetInTouchMsg')"
        class="text-body-1 font-weight-bold mt-8 mb-4"
        density="default"
        type="success"
        variant="tonal"
      >
      </v-alert>
    </template>
    <template #actions>
      <template v-if="emailSentSuccessfully">
        <VSpacer />
        <SecondaryVBtn
          :text="$t('Close')"
          class="align-self-center"
          @click="finaliseSupportRequest"
        />
        <v-spacer></v-spacer>
      </template>
      <template v-else>
        <TextVBtn @click="closeDialog">
          {{ $t('Cancel') }}
        </TextVBtn>
        <VeeSubmitButton style="margin-left: 16px" :text="$t('Send')" @click="submit" />
      </template>
    </template>
  </Dialog>
</template>
